/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type {
  AppointmentKind,
  AttentionReason,
  Concern,
  DayBlock,
  DeliveryStatus,
  InsuranceDecision,
  InsuranceExit,
  MessageChannel,
  MessageKind,
  MessageStatus,
  OnboardingEvent,
  OnboardingStatus,
  OnboardingStep,
  ProgressStep,
  ProviderCard,
  ProviderSpecialty,
  StaffOnboarding,
  Weekday,
} from "../api/onboarding";
import type { BadgeTone } from "../components/ui/Badge";

export const STATUS_LABELS: Record<OnboardingStatus, string> = {
  INVITED: "Invited",
  OPENED: "Opened",
  VERIFIED: "Verified",
  CONFIRMED: "Details confirmed",
  SCREENED: "Screened",
  ENROLLED: "Family created",
  BOOKED: "Booked",
  COMPLETED: "Done",
  NEEDS_CARE_TEAM: "Needs care team",
  PAUSED: "Paused",
  STOPPED: "Stopped",
  EXPIRED: "Unresponsive",
};

const ENDED: ReadonlySet<OnboardingStatus> = new Set(["COMPLETED", "STOPPED", "EXPIRED"]);

export function isEnded(status: OnboardingStatus): boolean {
  return ENDED.has(status);
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as OnboardingStatus] ?? "Unknown status";
}

export function statusTone(status: OnboardingStatus): BadgeTone {
  switch (status) {
    case "NEEDS_CARE_TEAM":
      return "warning";
    case "BOOKED":
    case "COMPLETED":
      return "success";
    case "STOPPED":
    case "EXPIRED":
      return "error";
    case "PAUSED":
      return "neutral";
    default:
      return "info";
  }
}

type AttentionCopy = { label: string; sentence: string };

export const ATTENTION_COPY: Record<AttentionReason, AttentionCopy> = {
  LANDLINE: {
    label: "Landline",
    sentence: "The phone on file can't receive texts. Call the family or add a mobile number.",
  },
  INVALID_CONTACT: {
    label: "No working contact",
    sentence: "Texts and emails to this family aren't arriving. Find a working contact, then resend the link.",
  },
  DOB_LOCKED: {
    label: "Date of birth locked",
    sentence: "The family entered a wrong date of birth five times. Confirm who they are, then unlock it.",
  },
  DUPLICATE: {
    label: "Duplicate",
    sentence: "These details look like accounts we already have. Attach the family to one or create a new family.",
  },
  PROVISIONING_FAILED: {
    label: "Account setup stuck",
    sentence: "We couldn't finish setting up the family's records. Finish it from the account page; the family carries on once every member links.",
  },
  WAITLIST: {
    label: "Waitlist",
    sentence: "No licensed provider had a time for this family. Find one and book on the phone.",
  },
  INTAKE_REQUESTED: {
    label: "Intake requested",
    sentence: "The family told us which times suit them. Find a time in those windows and book on the phone.",
  },
  BOOKING_FAILED: {
    label: "Booking failed",
    sentence: "The family's booking couldn't be made. Call them and book on the phone.",
  },
  INSURANCE_REVIEW: {
    label: "Insurance review",
    sentence: "The family's insurance needs a look before they continue.",
  },
  FAMILY_ASKED: {
    label: "Family wrote back",
    sentence: "The family replied to a text. Read it and get back to them today.",
  },
  STAFF: {
    label: "Held by staff",
    sentence: "A coordinator handed this family to the care team.",
  },
};

export function attentionCopy(reason: string): AttentionCopy {
  return (
    ATTENTION_COPY[reason as AttentionReason] ?? {
      label: "Needs a look",
      sentence: "This family needs a coordinator.",
    }
  );
}

export const STEP_LABELS: Record<OnboardingStep, string> = {
  DETAILS: "Details",
  CONCERNS: "Concerns",
  INSURANCE: "Insurance",
  SAFETY: "Confirmations",
  ENROLL: "Create the family",
  BOOK: "Book the first session",
  FORMS: "Intake forms",
};

export function stepLabel(step: string): string {
  return STEP_LABELS[step as OnboardingStep] ?? "Next step";
}

const PRE_ENROLLMENT: ReadonlySet<OnboardingStep> = new Set(["DETAILS", "CONCERNS", "INSURANCE", "SAFETY"]);

/** Nexus locks the four pre-enrollment steps once the family is enrolled: they were done to get there. */
export function stepDone({ step, state }: ProgressStep): boolean {
  return state === "DONE" || (state === "LOCKED" && PRE_ENROLLMENT.has(step));
}

/** When each step was finished, as the staff view dates it. */
export function stepTime(steps: StaffOnboarding["steps"], step: OnboardingStep): string | null {
  switch (step) {
    case "DETAILS":
      return steps.detailsSavedAt;
    case "CONCERNS":
      return steps.concernsSavedAt;
    case "INSURANCE":
      return steps.insuranceSavedAt;
    case "SAFETY":
      return steps.safetySavedAt;
    case "ENROLL":
      return steps.enrolledAt;
    case "BOOK":
      return steps.bookedAt;
    case "FORMS":
      return steps.formsOpenedAt;
  }
}

export const CONCERN_LABELS: Record<Concern, string> = {
  WORRIES_STRESS_ANXIETY: "Worries, stress, anxiety",
  SADNESS_DEPRESSION_GRIEF: "Sadness, depression, grief, or loss",
  BEHAVIOR_ATTENTION_SCHOOL: "Behavior, attention, or school performance",
  FAMILY_IDENTITY_RELATIONSHIPS: "Family, identity, or relationships",
  VIOLENCE_TRAUMA: "Violence or trauma",
  PASSIVE_SI_SELF_HARM: "Passive thoughts of suicide or self-harm",
  ACTIVE_DIVORCE_SEPARATION: "Active divorce or separation",
  ACTIVE_SI: "Active suicidal ideation (recent hospitalization or a plan)",
  OTHER: "Other",
};

/** Why the family can book only a consult, or null when they can book an intake. */
export function consultOnlyWhy(
  concerns: Concern[],
  safetyConfirmed: boolean | null,
): string | null {
  const reasons = [
    concerns.includes("ACTIVE_SI") ? "active suicidal ideation" : null,
    concerns.includes("ACTIVE_DIVORCE_SEPARATION") ? "an active divorce or separation" : null,
    safetyConfirmed === false ? "a No at the confirmations" : null,
  ].filter((reason): reason is string => reason !== null);
  return reasons.length === 0 ? null : `Because of ${joinWords(reasons)}.`;
}

export const EXIT_SENTENCES: Record<InsuranceExit, string> = {
  ENTER_PLAN: "No plan on file yet.",
  CHECKING: "Checking the coverage with the plan.",
  COVERED: "Covered and in network.",
  CONFIRM_LATER: "Plan on file. We confirm the coverage with the family before the first session.",
  OUT_OF_NETWORK: "The plan is out of network. The family can pay per session.",
  NOT_ACTIVE: "The plan isn't active. The family can pay per session.",
  REVIEW: "The care team reviews this plan before the family continues.",
  MEMBER_ID_NEEDED: "The plan was found. It needs the member ID from the card.",
  CARD_UNREADABLE: "The card photo couldn't be read.",
  NOT_FOUND: "No plan was found under the child's name.",
  UNMAPPED: "We don't work with this plan yet. The family can pay per session.",
  SELF_PAY: "Paying per session.",
};

export function exitSentence(exit: string): string {
  return EXIT_SENTENCES[exit as InsuranceExit] ?? "Insurance not settled yet.";
}

export const DECISION_LABELS: Record<InsuranceDecision, string> = {
  CONFIRMED: "Plan on file confirmed",
  ENTERED: "Entered by hand",
  DISCOVERED: "Found by search",
  CARD: "Read from the card",
  SELF_PAY: "Self-pay",
};

export const MESSAGE_KIND_LABELS: Record<MessageKind, string> = {
  INVITE: "Invite",
  FOLLOW_UP: "Reminder",
  INTAKE_CONFIRMATION: "Intake confirmation",
  CONSULT_CONFIRMATION: "Consult confirmation",
  SET_PASSWORD: "Set a password",
  CARE_TEAM_HANDOFF: "Care team will reach out",
  ENROLLMENT_REVIEW: "Enrollment under review",
  SLOT_LOST: "Time no longer available",
};

export const SPECIALTY_LABELS: Record<ProviderSpecialty, string> = {
  THERAPIST: "Therapist",
  PSYCHIATRIST: "Psychiatrist",
  PHYSICIAN: "Physician",
  PEDIATRICIAN: "Pediatrician",
  PARENT_COACH: "Parent coach",
  DIETITIAN: "Dietitian",
};

/** A family's messaging choice, in words; nothing when they made none. */
export function optedOutWords(optedOut: readonly MessageChannel[]): string | null {
  const texts = optedOut.includes("SMS");
  const email = optedOut.includes("EMAIL");
  if (texts && email) {
    return "Opted out of texts and email";
  }
  return texts ? "Opted out of texts" : email ? "Opted out of email" : null;
}

export function messageKindLabel(kind: string, sequence: number | null): string {
  const label = MESSAGE_KIND_LABELS[kind as MessageKind] ?? "Message";
  return kind === "FOLLOW_UP" && sequence !== null && sequence > 0 ? `Day ${sequence} reminder` : label;
}

export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  QUEUED: "Scheduled",
  SENDING: "Sending",
  SENT: "Sent",
  FAILED: "Failed",
  SUPPRESSED: "Held back",
  SKIPPED: "Skipped",
  CANCELLED: "Cancelled",
  UNKNOWN: "Unconfirmed",
};

export const DELIVERY_LABELS: Record<DeliveryStatus, string> = {
  DELIVERED: "Delivered",
  UNDELIVERED: "Not delivered",
  BOUNCED: "Bounced",
  DROPPED: "Dropped",
  DEFERRED: "Delayed",
  SPAM: "Marked as spam",
};

const SKIP_LABELS: Record<string, string> = {
  ADDRESS_CHANGED: "The contact changed",
  NO_PHONE: "No phone on file",
  NO_EMAIL: "No email on file",
  LINK_ROTATED: "The link was replaced",
  LINK_EXPIRED: "The link expired",
  RULE_CHANGED: "Schedule changed",
  ONBOARDING_EXPIRED: "The family was unresponsive",
  UNKNOWN_KIND: "Not a message we send",
  ONBOARDING_NOT_FOUND: "The onboarding is gone",
};

const ONBOARDING_SKIP_PREFIX = "ONBOARDING_";

export function skipReasonLabel(reason: string): string {
  const known = SKIP_LABELS[reason];
  if (known !== undefined) {
    return known;
  }
  if (reason.startsWith(ONBOARDING_SKIP_PREFIX)) {
    const status = reason.slice(ONBOARDING_SKIP_PREFIX.length);
    if (status in STATUS_LABELS) {
      return `The onboarding was ${statusLabel(status).toLowerCase()}`;
    }
  }
  return "Not sent";
}

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

const BLOCK_LABELS: Record<DayBlock, string> = {
  MORNING: "mornings",
  AFTERNOON: "afternoons",
  EVENING: "evenings",
};

export function preferencesSentence(preferences: StaffOnboarding["preferences"]): string | null {
  if (preferences === null) {
    return null;
  }
  const days = preferences.weekdays.map((day) => WEEKDAY_LABELS[day]);
  const blocks = preferences.blocks.map((block) => BLOCK_LABELS[block]);
  const when = [
    days.length > 0 ? joinWords(days) : "Any day",
    blocks.length > 0 ? joinWords(blocks) : "any time",
  ];
  return `${when[0]}, ${when[1]}.`;
}

/** The account ids a held enrollment names in its detail ("possible duplicate of accounts 12, 34"). */
export function lookAlikeAccounts(detail: string | null): number[] {
  if (detail === null || !detail.startsWith("possible duplicate of accounts")) {
    return [];
  }
  return (detail.match(/\d+/g) ?? []).map(Number);
}

/** The plain sentence under a DUPLICATE badge when the hold is not about look-alikes. */
export function duplicateDetailSentence(detail: string | null): string | null {
  if (detail === "email belongs to another kind of account") {
    return "The caregiver's email belongs to another kind of account.";
  }
  if (detail === "email belongs to an account that cannot sign in") {
    return "The caregiver's email belongs to a closed account.";
  }
  return null;
}

/** Why the enrollment is held, in the hold's own words. */
export function enrollmentHoldSentence(detail: StaffOnboarding): string {
  return duplicateDetailSentence(detail.attentionDetail) ?? ATTENTION_COPY.DUPLICATE.sentence;
}

/**
 * Whether the hold's words are a coordinator's note rather than the family's own text: the entry
 * that started the hold says who raised it, and a reply during the hold leaves the note alone.
 */
export function attentionDetailFromStaff(detail: StaffOnboarding): boolean {
  const hold = detail.events.find((event) => statusTo(event) === "NEEDS_CARE_TEAM");
  return hold?.field === "action.hand-to-care-team";
}

/** The status an entry moved the onboarding to, when it moved it. */
function statusTo(event: OnboardingEvent): string | null {
  const move = event.detail?.["status"];
  return typeof move === "object" && move !== null ? ((move as { to?: string }).to ?? null) : null;
}

export function actorLabel(event: OnboardingEvent): string {
  switch (event.actorKind) {
    case "FAMILY":
      return "Family";
    case "STAFF":
      return "Staff";
    default:
      return "System";
  }
}

function detailText(event: OnboardingEvent, key: string): string | null {
  const value = event.detail?.[key];
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : null;
}

const NOTE_EDIT_WORDS: Record<string, string> = {
  ADDED: "Added a note",
  EDITED: "Edited the note",
  REMOVED: "Removed the note",
};

/** One change-log entry in plain words. */
export function eventSentence(event: OnboardingEvent): string {
  const reason = detailText(event, "reason");
  const to = statusTo(event);
  const hold = to === "NEEDS_CARE_TEAM" && reason !== null ? attentionCopy(reason).label.toLowerCase() : null;
  const act = actSentence(event, reason);
  if (event.field === "status") {
    return hold !== null ? `Needs care team: ${hold}` : `Now ${statusLabel(to ?? "").toLowerCase()}`;
  }
  return hold !== null && !SAYS_ITS_HOLD.has(event.field) ? `${act} · needs care team: ${hold}` : act;
}

/** The acts that name the hold they raise, so it is not said twice. */
const SAYS_ITS_HOLD: ReadonlySet<string> = new Set(["action.hand-to-care-team", "action.insurance-review"]);

function actSentence(event: OnboardingEvent, reason: string | null): string {
  switch (event.field) {
    case "created":
      return "Onboarding started";
    case "link.opened":
      return "Opened the link";
    case "link.resent":
      return "Sent a fresh link";
    case "dob.attempt": {
      const attempt = detailText(event, "attempt");
      return attempt === null
        ? "Entered a wrong date of birth"
        : `Entered a wrong date of birth (try ${attempt})`;
    }
    case "dob.unlocked":
      return "Unlocked the date of birth";
    case "consent":
      return "Agreed to texts";
    case "details.saved":
      return "Confirmed their details";
    case "concerns.saved":
      return "Saved their concerns";
    case "insurance.saved":
      return event.newValue !== null && event.newValue in DECISION_LABELS
        ? `Saved insurance: ${DECISION_LABELS[event.newValue as InsuranceDecision].toLowerCase()}`
        : "Saved insurance";
    case "insurance.reopened":
      return "Insurance reopened for an update";
    case "safety.saved":
      return event.detail?.["confirmed"] === false
        ? "Answered No at the confirmations"
        : "Answered Yes at the confirmations";
    case "referral.reseeded":
      return "Referral edits copied in";
    case "sms.stop":
      return "Replied STOP to texts";
    case "sms.reply":
      return "Family replied";
    case "message.reaction":
      return "A text couldn't be delivered";
    case "provisioning":
      return "Account setup updated";
    case "preferences.saved":
      return "Said none of the times work";
    case "booking.requested":
      return event.newValue === "CONSULT" ? "Asked for a consult" : "Asked for a first session";
    case "booking.conflict":
      return "The time was just taken";
    case "booking.failed":
      return "The booking failed";
    case "booked":
      return "Booked";
    case "zone.chosen":
      return `Time zone set to ${event.newValue ?? "unknown"}`;
    case "note.saved":
      return NOTE_EDIT_WORDS[detailText(event, "edit") ?? ""] ?? "Updated the note";
    case "appointment.linked":
      return "Appointment linked";
    case "forms.opened":
      return "Opened the intake forms";
    case "action.pause":
      return "Paused";
    case "action.resume":
      return "Resumed";
    case "action.stop":
      return "Stopped";
    case "action.hand-to-care-team":
      return reason === null
        ? "Handed to the care team"
        : `Handed to the care team: ${attentionCopy(reason).label.toLowerCase()}`;
    case "action.insurance-review":
      return "Sent insurance for review";
    case "verified":
      return "Verified who they are";
    case "action.enroll":
      if (detailText(event, "held") !== null) {
        return "Asked to create the family";
      }
      return detailText(event, "family") === "ATTACHED"
        ? "Attached to an existing family"
        : detailText(event, "family") === "RETURNING"
          ? "Joined a returning family"
          : "Created the family";
    case "action.attach-family":
      return "Attached to an existing family";
    default:
      return "Updated";
  }
}

/** Every field the timeline words; a test holds it to the nexus change-log fields. */
export const EVENT_FIELDS = [
  "status",
  "created",
  "zone.chosen",
  "link.opened",
  "verified",
  "link.resent",
  "dob.attempt",
  "dob.unlocked",
  "consent",
  "details.saved",
  "concerns.saved",
  "insurance.saved",
  "insurance.reopened",
  "safety.saved",
  "referral.reseeded",
  "sms.stop",
  "sms.reply",
  "message.reaction",
  "provisioning",
  "preferences.saved",
  "booking.requested",
  "booking.conflict",
  "booking.failed",
  "note.saved",
  "booked",
  "appointment.linked",
  "forms.opened",
  "action.pause",
  "action.resume",
  "action.stop",
  "action.hand-to-care-team",
  "action.insurance-review",
  "action.enroll",
  "action.attach-family",
] as const;

/** What answers each hold: the next step a coordinator takes to release it. */
export const HOLD_ANSWER: Record<AttentionReason, "settleDuplicate" | "unlockDob" | "resend" | "resume"> = {
  LANDLINE: "resend",
  INVALID_CONTACT: "resend",
  DOB_LOCKED: "unlockDob",
  DUPLICATE: "settleDuplicate",
  PROVISIONING_FAILED: "resume",
  WAITLIST: "resume",
  INTAKE_REQUESTED: "resume",
  BOOKING_FAILED: "resume",
  INSURANCE_REVIEW: "resume",
  FAMILY_ASKED: "resume",
  STAFF: "resume",
};

/**
 * What a coordinator may do with the onboarding as it stands. Pause and resume are about the
 * outreach cadence: pause holds the texts and emails while the family's link keeps working, and
 * resume starts them again. Resume also releases a care-team hold that has no action of its own;
 * a duplicate, a locked date of birth and a dead number each have theirs.
 */
type ActionAvailability = {
  pause: boolean;
  resume: boolean;
  /** True when the resume releases a care-team hold rather than ending a pause. */
  resumeReleasesHold: boolean;
  stop: boolean;
  handOff: boolean;
  note: boolean;
  unlockDob: boolean;
  resendLink: boolean;
  copyLink: boolean;
  settleDuplicate: boolean;
  phone: boolean;
};

/**
 * Whether the family holds a booked session: onboarding's job is done, whatever hold sits on top of
 * it, and the journey is read-only from here.
 */
export function isBooked(onboarding: Pick<StaffOnboarding["onboarding"], "booking">): boolean {
  return onboarding.booking.booked;
}

/**
 * What can be done now, so a button never offers what nexus would refuse. Once the family has booked
 * the journey is read-only: their link and their messages carry on, and nobody acts on it from here.
 * Resume and Unlock stay on a hold, since they give the family its forms back. An Unresponsive family
 * can be sent a new link, which brings them back where they left off; a Stop is final. When nothing
 * can carry a new link, a link that works stays as it is and only a dead one is remade.
 */
export function availableActions(staff: StaffOnboarding): ActionAvailability {
  const { status, attentionReason, caregiver } = staff.onboarding;
  const ended = isEnded(status);
  const booked = isBooked(staff.onboarding);
  const open = !ended && !booked;
  const holding = status === "PAUSED" || status === "NEEDS_CARE_TEAM";
  const releasesHold =
    status === "NEEDS_CARE_TEAM" && attentionReason !== null && HOLD_ANSWER[attentionReason] === "resume";
  const resume = status === "PAUSED" || releasesHold;
  const copyLink =
    open &&
    staff.linkExpiresAt !== null &&
    Date.parse(staff.linkExpiresAt) > Date.now() &&
    staff.messages.some((message) => message.kind === "INVITE");
  const hasAddress = caregiver.phone !== null || caregiver.email !== null;
  const reachable = resendCarriers(staff) !== null;
  return {
    pause: open && !holding,
    resume: resume && staff.dobLockedAt === null,
    resumeReleasesHold: releasesHold,
    stop: open,
    handOff: open && status !== "NEEDS_CARE_TEAM",
    note: open,
    unlockDob: staff.dobLockedAt !== null && !ended,
    resendLink: (open || status === "EXPIRED") && staff.stage !== "ACTIVE" && hasAddress && (reachable || !copyLink),
    copyLink,
    settleDuplicate: holding && attentionReason === "DUPLICATE",
    phone: open,
  };
}

/**
 * The channels that can carry a new link right now, in words, as nexus's contactability answers.
 * Null when nothing can: nexus then leaves a working link alone, and a new one has to be copied and
 * shared by hand.
 */
export function resendCarriers({ reachable }: Pick<StaffOnboarding, "reachable">): string | null {
  const text = reachable.includes("SMS");
  const email = reachable.includes("EMAIL");
  if (text && email) {
    return "text and email";
  }
  return text ? "text" : email ? "email" : null;
}

/** A US number as people write it; anything else as stored. */
export function displayPhone(phone: string | null): string | null {
  const digits = phone?.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return digits ? `(${digits[1]}) ${digits[2]}-${digits[3]}` : phone;
}

/** The provider as the family sees them: name, then credential when there is one. */
export function providerLine(card: ProviderCard): string {
  return [card.displayName, card.credential].filter(Boolean).join(", ");
}

export function bookingKindLabel(kind: AppointmentKind | null): string {
  return kind === "CONSULT" ? "Consult call" : "First session";
}

export function bookingStatusLabel(booking: StaffOnboarding["onboarding"]["booking"]): string {
  if (booking.booked) {
    return "Booked";
  }
  switch (booking.state) {
    case "REQUESTED":
      return "Requested, confirming with the calendar";
    case "FAILED":
      return "The booking failed";
    case "CONFLICT":
      return "The time was taken";
    default:
      return "Not booked yet";
  }
}

const MEMBER_STATUS_LABELS: Record<string, string> = {
  PENDING: "Setting up",
  LINKED: "Linked",
  REFUSED: "Refused by the records system",
  FAILED: "Failed",
};

export function memberStatusLabel(status: string): string {
  return MEMBER_STATUS_LABELS[status] ?? "Setting up";
}

function joinWords(words: string[]): string {
  if (words.length <= 1) {
    return words.join("");
  }
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}
